import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { catchError, map, Observable, retry, throwError, timer } from 'rxjs';
import { GsStructPage } from '../../models';
import { IInit } from '../../models/structure/init';
import { PREFERRED_USER_LANGUAGE } from '../../services/language/language.service';
import { DataProvider } from '../basic/data.service';

/** Retries of a page request that failed with a transient error. */
const PAGE_RETRY_COUNT = 3;
/** Delay before the first page retry (ms), doubled for every further retry. */
const PAGE_RETRY_BASE_DELAY_MS = 500;

/**
 * Whether a failed request may succeed when repeated: backend unreachable, timeout, rate limit or 5xx.
 * @param error The error of the failed request.
 * @returns True for a transient error.
 */
function isTransientHttpError(error: unknown): boolean {
  if (!(error instanceof HttpErrorResponse)) return false;
  const { status } = error;
  return (
    status === 0 ||
    status === HttpStatusCode.RequestTimeout ||
    status === HttpStatusCode.TooManyRequests ||
    status >= HttpStatusCode.InternalServerError
  );
}

/**
 * Backoff of a failed page request: transient errors are retried after 0.5 s, 1 s and 2 s, any
 * other error (e.g. 404) fails right away.
 * @param error The error of the failed request.
 * @param retryCount Number of the upcoming retry, starting at 1.
 * @returns A timer that releases the retry, or the error to fail with.
 */
function pageRetryDelay(error: unknown, retryCount: number): Observable<number> {
  if (!isTransientHttpError(error)) return throwError(() => error);
  return timer(PAGE_RETRY_BASE_DELAY_MS * 2 ** (retryCount - 1));
}

@Injectable({
  providedIn: 'root'
})
export class CmsProvider extends DataProvider<GsStructPage> {
  // Services
  /** The visitor's preferred language, the same on the server and in the hydrating browser */
  private readonly preferredUserLanguage = inject(PREFERRED_USER_LANGUAGE);

  // Properties & Signals
  override endpoint = 'f/cms/';

  // Methods
  /**
   * Gets the page to render by the given url
   * @param url to get the page by
   * @returns the page to render
   */
  getPageByUrl(url: string): Observable<GsStructPage> {
    const userLang = this.preferredUserLanguage;
    return this.http
      .get<GsStructPage>(this.endpointWithUrl, {
        ...this.httpOptionsWithContent,
        params: {
          userLanguages: userLang,
          url,
          cached: this.environment.env.ENVIRONMENT === 'production',
          // The backend only injects the tracking snippets (GTM/GA4 container)
          // into headerJavaScript when `tracking=true`. Default to production
          // only, but allow an explicit env override to test tracking in dev.
          tracking: this.environment.env.TRACKING ?? this.environment.env.ENVIRONMENT === 'production'
        }
      })
      .pipe(
        map((data) => {
          return DataProvider.mapCasesReverse(data);
        })
      )
      .pipe(retry({ count: PAGE_RETRY_COUNT, delay: pageRetryDelay }), catchError(this.errorService.convert));
  }

  /**
   * Initializes the app by fetching all necessary data for the app initialization, such as menus and link targets
   * @returns the init data for the app initialization
   */
  init(): Observable<IInit> {
    return this.http
      .post<IInit>(
        this.endpointWithUrl + 'init/', // The endpoint you're hitting to fetch the init data
        {
          Menus: this.environment.env.MENUS, // Requesting all relevant menus
          LinkTargets: this.environment.env.ACTIONS, // Requesting all relevant link targets
          WithChainStores: this.environment.env.WITH_CHAINSTORES, // Requesting if the chain stores should be loaded
          WithAppLinks: this.environment.env.WITH_APPLINKS // Requesting if the app links should be loaded
        },
        {
          ...this.httpOptionsWithContent, // HTTP options, such as headers
          params: {
            cached: this.environment.env.ENVIRONMENT === 'production' // Pass a `cached` param based on the environment
          }
        }
      )
      .pipe(
        map((data) => {
          return DataProvider.mapCasesReverse(data);
        })
      )
      .pipe(retry(1), catchError(this.errorService.convert));
  }
}
